// Jitter-buffer drain sizing for the SSE token stream (sendMessage thunk).
//
// MIDAS-STREAM-DRAIN (2026-11-02): the buffer used to release a FIXED 3 chars
// every 30 ms = 100 chars/s. A live LLM produces ~250-350 chars/s, so the
// queue grew for the whole answer and the end-of-stream `flushQueue()` (and
// every loop_progress flush) then dumped the backlog in one go: "a sentence
// types out, then the rest appears at once". The release size now scales with
// the backlog so the visible text trails arrival by ~CATCHUP_TICKS ticks
// (~240 ms) at any producer rate — still smooth for slow trickles (the 3-char
// floor), never building a backlog that has to be dumped later.
//
// Pure + unit-tested (__tests__/streamDrain.test.ts).

/** Minimum chars released per tick (the original fixed rate). */
export const DRAIN_MIN_CHARS_PER_TICK = 3;
/** Backlog is released over roughly this many ticks (30 ms each). */
export const DRAIN_CATCHUP_TICKS = 8;

/**
 * How many chars of `queue` to release this tick. Never splits a UTF-16
 * surrogate pair (emoji), so an intermediate render never shows a lone
 * surrogate.
 */
export const drainChunkSize = (queue: string): number => {
  const len = queue.length;
  if (len === 0) return 0;
  let n = Math.max(DRAIN_MIN_CHARS_PER_TICK, Math.ceil(len / DRAIN_CATCHUP_TICKS));
  if (n >= len) return len;
  const code = queue.charCodeAt(n - 1);
  if (code >= 0xd800 && code <= 0xdbff) n += 1; // high surrogate → keep its pair
  return n;
};
